import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import "../assets/css/style.css";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { FaPlus } from "react-icons/fa6";
import Modal from "../components/ui/Modal";
import {
  useGetSuburbQuery,
  useGetSuburbByIdQuery,
  useCreateSuburbMutation,
  useUpdateSuburbMutation,
  useDeleteSuburbMutation,
} from "apis/suburb";
import SuburbEdit from "components/suburb/SuburbEdit";
import SuburbView from "components/suburb/SuburbView";
import Table from "components/ui/Table";

const Suburbs = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const pageSize = 10;

  const {
    data,
    isPending: isDataPending,
  } = useGetSuburbQuery();

  // Full doc for the view/edit modals — fetched per-row on click, so the list
  // stays lightweight (suburb/postcode only).
  const { data: suburbDetail, isPending: detailLoading } =
    useGetSuburbByIdQuery(selectedId);

  const { mutate: createSuburb, isPending: isCreatePending } = useCreateSuburbMutation();
  const { mutate: updateSuburb } = useUpdateSuburbMutation();
  const { mutate: deleteSuburb } = useDeleteSuburbMutation();

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "suburb",
        header: "Suburb",
        cell: (row) => (
          <span className="font-medium">{row.suburb}</span>
        ),
        isFilter: true,
      },
      {
        accessorKey: "postcode",
        header: "Postcode",
        isFilter: true,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "120px",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit Suburb"
            >
              <FiEdit2 fontSize={20} />
            </button>
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsDeleteOpen(true);
              }}
              className="delete-icon"
              title="Delete Suburb"
            >
              <FiTrash2 fontSize={20} />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  if (isDataPending) return <PageLoader />;

  const selectedSuburb = data?.find((s) => s._id === selectedId);

  const handleEditSubmit = (formData) => {
    updateSuburb({ _id: selectedId, ...formData });
    setIsEditOpen(false);
  };

  const handleCreateSubmit = (formData) => {
    createSuburb(formData, { onSuccess: () => setIsAddOpen(false) });
  };

  const handleDelete = () => {
    deleteSuburb(selectedId);
    setIsDeleteOpen(false);
  };

  const handleViewRowClick = (row) => {
    setSelectedId(row._id);
    setIsViewOpen(true);
  };

  const AddSuburbButton = () => (
    <button
      className="black-button flex items-center"
      onClick={() => setIsAddOpen(true)}
    >
      <FaPlus size={14} className="mr-1" />
      Add Suburb
    </button>
  );

  return (
    <>
      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add Suburb"
        className="max-w-2xl"
      >
        <SuburbEdit
          initialData={{}}
          onSubmit={handleCreateSubmit}
          isLoading={isCreatePending}
        />
      </Modal>

      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Suburb"
        className="max-w-2xl"
      >
        {detailLoading || !suburbDetail ? (
          <div className="py-8 text-center text-darkgray">Loading…</div>
        ) : (
          <SuburbEdit initialData={suburbDetail} onSubmit={handleEditSubmit} />
        )}
      </Modal>

      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete Suburb"
      >
        <div>
          Are you sure you want to delete{" "}
          <strong>{selectedSuburb?.suburb}</strong>?
          <div className="w-full flex gap-2 justify-end py-4">
            <button
              onClick={() => setIsDeleteOpen(false)}
              className="gray-button"
            >
              No
            </button>
            <button onClick={handleDelete} className="black-button">
              Yes
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title="Suburb Details"
        className="max-w-3xl"
      >
        {detailLoading || !suburbDetail ? (
          <div className="py-8 text-center text-darkgray">Loading…</div>
        ) : (
          <SuburbView suburb={suburbDetail} />
        )}
      </Modal>

      <div className="container mx-auto">
        <Table
          title="Suburbs"
          columns={columns}
          data={data || []}
          pageSize={pageSize}
          ButtonCmp={AddSuburbButton}
          onRowClick={handleViewRowClick}
        />
      </div>
    </>
  );
};

export default Suburbs;